import {
  type Vec3,
  encodeBlock,
  getTerrainBlockType,
  getTerrainBlockTypes,
} from "@dust/world/internal";
import { getWorldAddress } from "@permutation-city/common";
import type { PublicClient } from "viem";
import {
  fetchDozerQuery,
  formatDozerResponse,
} from "../../../queries/useDozerQuery";
import type { BlueprintItem } from "../useBlueprintQuery";

export async function getObjectTypeAt(
  publicClient: PublicClient,
  pos: Vec3,
): Promise<number> {
  const data = await fetchDozerQuery(
    `SELECT "objectType" FROM EntityObjectType WHERE "entityId" = '${encodeBlock(pos)}'`,
  );
  const formattedResponse = formatDozerResponse<{ objectType: number }[][]>({
    data,
  });
  let objectTypeId = formattedResponse[0]?.[0]?.objectType;

  if (!objectTypeId) {
    const worldAddress = getWorldAddress();
    objectTypeId = await getTerrainBlockType(publicClient, worldAddress, pos);
  }
  return objectTypeId;
}

export async function getObjectTypesAt(
  publicClient: PublicClient,
  positions: Vec3[],
) {
  if (positions.length === 0) {
    return [];
  }
  const worldAddress = getWorldAddress();
  const encodedEntityObjectTypesResponse = await fetchDozerQuery(
    `SELECT "entityId", "objectType" FROM "EntityObjectType" WHERE "entityId" IN (${positions
      .map((pos) => `decode('${encodeBlock(pos).replace("0x", "")}', 'hex')`)
      .join(",")})`,
  );
  const encodedEntityObjectTypes = formatDozerResponse<
    { objectType: number }[][]
  >({
    data: encodedEntityObjectTypesResponse,
  })[0];

  const entityObjectTypes = encodedEntityObjectTypes.map((item) => {
    return {
      objectTypeId: Number(item.objectType),
      position: positions.find((pos) => encodeBlock(pos) === item.entityId),
    };
  });

  const missingPositions = positions.filter(
    (pos) => !entityObjectTypes.some((item) => item.position === pos),
  );

  const terrainObjectTypes = await getTerrainBlockTypes(
    publicClient,
    worldAddress,
    missingPositions,
  );

  const mappedTerrainObjectTypes = terrainObjectTypes.map(
    (objectTypeId, idx) => {
      return {
        objectTypeId,
        position: missingPositions[idx],
      };
    },
  );

  return [...entityObjectTypes, ...mappedTerrainObjectTypes] as BlueprintItem[];
}
